import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useGetModalActions } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { City } from 'utils/constants/location_legacy';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { isCityMatch } from 'utils/misc';

interface Props {
  document: any;
  title: string;
  isLoading: boolean;
}

const TradePermitModal = ({ document, title, isLoading }: Props) => {
  const { Skeleton, ...modalProps } = useGetModalActions(isLoading);

  const permitUrl = document?.document_url;
  const { permitnum, description, workclassgroup, permitclass, permitclassmapped } =
    document?.metadata || {};

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(document?.metadata?.relatedcontacts || []),
    [document?.metadata],
  );

  // Determine if the current permit is for Dallas
  const isDallas = isCityMatch(document?.city, [City.DALLAS]);

  // Concatenate description with proposedusedescription if available
  const combinedDescription = `
    ${description || ''}${
      document?.metadata.proposedusedescription
        ? ` ${document?.metadata.proposedusedescription}`
        : ''
    }`.trim();

  let tradeWorkTypeContent;
  if (isDallas) {
    // Choose the first defined field among permitclass, permitclassmapped, or workclassgroup
    const workType = permitclass || permitclassmapped || workclassgroup;
    tradeWorkTypeContent = workType ? (
      <Text textTransform="capitalize">{workType}</Text>
    ) : (
      <EmptyField />
    );
  } else {
    tradeWorkTypeContent = workclassgroup ? (
      <Text textTransform="capitalize">{workclassgroup}</Text>
    ) : (
      <EmptyField />
    );
  }

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type={title}>
          {permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={document?.metadata?.parcel_address ?? ''}
          city={document?.city ?? ''}
          state_code={document?.state_code ?? ''}
          isLoading={isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={document?.companies}
          documentId={document?.uuid || document?.document_uuid || document?.id}
          prefetchedAiContactSuggestionsByRelatedContactId={
            document?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />
        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {document?.metadata.projectname ? (
                <Text fontWeight="bold">{document.metadata.projectname}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>
        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {combinedDescription ? <Text>{combinedDescription}</Text> : <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>
        <InfoModal.Row hideDivider>
          <Label>Work Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {tradeWorkTypeContent}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>
        <ModalStatus activities={document?.activities} Skeleton={Skeleton} />
        {isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!isLoading && permitUrl && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1}>
                <ExternalLink
                  to={permitUrl}
                  onClick={() => trackProjectMaterials(permitUrl)}
                >
                  View application
                </ExternalLink>
              </Skeleton>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};

export default TradePermitModal;
